import { Button, Checkbox, Dialog, DialogContent, FormControlLabel } from '@mui/material';
import { FC, useContext, useRef, useState } from 'react';
import { AppContext } from 'templates/AppContextProvider';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { TermsParser } from 'organisms/u002/TermsParser';
import { Form } from 'templates/Form';
import LoadingModal from 'organisms/LoadingModal';
import { useLatestGetConsentHooks } from 'hooks/U015/useLatestGetConsentHooks';
import { useUpdateConsentHooks } from 'hooks/U015/useUpdateConsentHooks';
import MessageModal from 'organisms/MessageModal';

export const PrivacyNoticeReAgreement: FC<{ onComplete: () => void }> = ({ onComplete }) => {
  const { t: tConsent } = useTranslationWithLang('consent');
  const { t: tCommons } = useTranslationWithLang('commons');
  const [checked, setChecked] = useState(false);
  const [open, setOpen] = useState(true);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);
  const termsRef = useRef<HTMLDivElement>(null);
  const { currentLang, userId } = useContext(AppContext);
  const [data, loading, err] = useLatestGetConsentHooks({
    lang: currentLang,
    consent_type: '1', // 1: プライバシーポリシー
  });
  const [updateConsent, updateLoading] = useUpdateConsentHooks();
  const [errorMessage, setErrorMessage] = useState('');

  if (err && !loading) {
    return (
      <MessageModal
        message={tConsent('TU015_001')}
        closeLabel={tConsent('TU015_002')}
        onClose={() => {
          setErrorMessage('');
          onComplete();
        }}
      />
    );
  }

  const onSubmit = () => {
    if (!data || !checked) return;
    updateConsent({
      request: {
        user_id: userId,
        consent_type: '1', // 1: プライバシーポリシー
        revision: data.revision.toString(),
      },
      onSuccess: () => {
        setOpen(false);
        onComplete();
      },
      onError: (error) => {
        console.log('error', error);
        setErrorMessage(tConsent('TU015_001'));
      },
    });
  };

  const handleDialogEntered = () => {
    const checkScrollable = () => {
      if (termsRef.current) {
        const { scrollHeight, clientHeight } = termsRef.current;
        if (scrollHeight <= clientHeight) {
          setIsScrolledToEnd(true);
        }
      }
    };

    const handleScroll = () => {
      if (termsRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = termsRef.current;
        if (scrollTop + clientHeight >= scrollHeight - 1) {
          setIsScrolledToEnd(true);
        }
      }
    };

    const termsElement = termsRef.current;
    if (termsElement) {
      const resizeObserver = new ResizeObserver(checkScrollable);
      resizeObserver.observe(termsElement);
      termsElement.addEventListener('scroll', handleScroll);
      checkScrollable();
    }
  };

  return (
    <>
      {errorMessage && (
        <MessageModal
          message={errorMessage}
          closeLabel={tConsent('TU015_002')}
          onClose={() => setErrorMessage('')}
        />
      )}
      <Dialog
        open={open}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          onEntered: handleDialogEntered,
        }}
        PaperProps={{
          style: {
            color: 'var(--c-text)',
            borderRadius: '10px',
            backgroundColor: 'var(--c-background2)',
            boxShadow: '0 3px 10px rgba(0, 0, 0, 0.2)',
          },
        }}
      >
        {(loading || updateLoading) && <LoadingModal />}
        <DialogContent>
          <Form title={tConsent('TU015_005')}>
            <div ref={termsRef} className="max-h-96 overflow-y-scroll my-10">
              <TermsParser text={data?.consent ?? ''} />
            </div>
            <FormControlLabel
              control={
                <Checkbox
                  color="secondary"
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                  disabled={!isScrolledToEnd}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.currentTarget.disabled) {
                      setChecked(!checked);
                    }
                  }}
                  inputProps={{
                    'aria-label': tCommons('TU000_088'),
                    'aria-describedby': 'consent-checkbox-description',
                  }}
                />
              }
              label={tConsent('TU015_006')}
              className="text-[--c-text] flex justify-center items-center"
            />

            <div className={'flex justify-center items-center'}>
              <Button
                className={'w-[9rem]'}
                variant="contained"
                onClick={() => {
                  onSubmit();
                }}
                disabled={!checked}
                style={{
                  fontWeight: 'bold',
                  color: 'var(--c-button-text)',
                  borderRadius: '300px',
                  padding: '1rem 2rem',
                  background: checked ? 'var(--c-button-background)' : undefined,
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = 'var(--c-button-background-hover)')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = 'var(--c-button-background)')
                }
              >
                {tConsent('TU015_006')}
              </Button>
            </div>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
};
